/**
 * Single source of truth for the join funnel.
 *
 * Responsibilities:
 * - Ensure a funnel session token exists (mints one on first call).
 * - Persist the token to localStorage so it survives reloads and the Stripe
 *   3DS redirect on the payment page.
 * - Hydrate the in-memory lead from the backend (and from React Query cache
 *   on subsequent steps).
 * - Expose a blur-triggered partial update mutation.
 * - Expose loading/error/state in a shape components can consume directly.
 */

import { useGetMyFunnelLeadV1FunnelSessionsMeLeadGet, useUpdateMyFunnelLeadV1FunnelSessionsMeLeadPatch } from '@/api/generated-catch-backend/funnel/funnel';
import {
  createFunnelSessionV1FunnelSessionsPost,
  getGetMyFunnelLeadV1FunnelSessionsMeLeadGetQueryKey,
} from '@/api/generated-catch-backend/funnel/funnel';
import type {
  FunnelLeadResponse,
  UpdateFunnelLeadRequest,
} from '@/api/generated-catch-backend/model';
import { FUNNEL_SESSION_ID_KEY, FUNNEL_SESSION_TOKEN_KEY, type PositioningVariant } from '@/constants/funnel';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

interface UseFunnelLeadResult {
  /** Current lead (undefined until first fetch resolves). */
  lead: FunnelLeadResponse | undefined;
  /** True while the lead is loading or the session is being minted. */
  isLoading: boolean;
  /** Whether the funnel session token is in place. */
  hasSession: boolean;
  /** Update one or more lead fields. Resolves with the updated lead. */
  updateLead: (update: UpdateFunnelLeadRequest) => Promise<FunnelLeadResponse>;
  /** True while a PATCH is in flight. */
  isUpdating: boolean;
}

/**
 * In-flight mint promise shared across concurrent callers. Strict-mode
 * double-mounts and parallel hook invocations would otherwise race two
 * POST /v1/funnel-sessions requests; we want exactly one network call per
 * fresh load.
 */
let inFlightMint: Promise<string> | null = null;

/**
 * Mints (or rotates) the funnel session token. Stores it in localStorage so
 * subsequent calls go through the auth interceptor without round-trips. If a
 * mint is already in flight, returns its promise rather than starting a new
 * request.
 */
async function mintFunnelSession(positioningVariant?: PositioningVariant): Promise<string> {
  // Re-check localStorage in case a concurrent caller finished while we
  // were queued — saves a round-trip when StrictMode double-mounts hooks.
  const existing = localStorage.getItem(FUNNEL_SESSION_TOKEN_KEY);
  if (existing) return existing;

  if (inFlightMint) return inFlightMint;

  inFlightMint = (async () => {
    try {
      const response = await createFunnelSessionV1FunnelSessionsPost({
        positioningVariant: positioningVariant ?? null,
      });
      localStorage.setItem(FUNNEL_SESSION_TOKEN_KEY, response.funnelSessionToken);
      localStorage.setItem(FUNNEL_SESSION_ID_KEY, response.funnelSessionId);
      return response.funnelSessionToken;
    } finally {
      inFlightMint = null;
    }
  })();

  return inFlightMint;
}

export function getFunnelSessionToken(): string | null {
  return localStorage.getItem(FUNNEL_SESSION_TOKEN_KEY);
}

export function getFunnelSessionId(): string | null {
  return localStorage.getItem(FUNNEL_SESSION_ID_KEY);
}

export function clearFunnelSessionToken(): void {
  localStorage.removeItem(FUNNEL_SESSION_TOKEN_KEY);
  localStorage.removeItem(FUNNEL_SESSION_ID_KEY);
}

/**
 * Ensures a funnel session exists; mints one on first invocation. The token
 * is persisted to localStorage.
 *
 * @param positioningVariant - The variant to attach to a freshly-minted
 *   session. Ignored if a session already exists; in that case the variant
 *   was already locked in at mint time.
 */
export function useEnsureFunnelSession(positioningVariant?: PositioningVariant): {
  hasSession: boolean;
  isInitializing: boolean;
} {
  const [hasSession, setHasSession] = useState(() => Boolean(getFunnelSessionToken()));
  const [isInitializing, setIsInitializing] = useState(() => !getFunnelSessionToken());

  useEffect(() => {
    if (hasSession) return;
    let cancelled = false;
    void (async () => {
      try {
        await mintFunnelSession(positioningVariant);
        if (!cancelled) {
          setHasSession(true);
        }
      } finally {
        if (!cancelled) {
          setIsInitializing(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasSession, positioningVariant]);

  return { hasSession, isInitializing };
}

export function useFunnelLead(): UseFunnelLeadResult {
  const queryClient = useQueryClient();
  const hasSession = Boolean(getFunnelSessionToken());

  const { data: lead, isLoading } = useGetMyFunnelLeadV1FunnelSessionsMeLeadGet({
    query: {
      enabled: hasSession,
      // Lead changes are driven by our own PATCHes — don't refetch on focus.
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  });

  const updateMutation = useUpdateMyFunnelLeadV1FunnelSessionsMeLeadPatch({
    mutation: {
      onSuccess: (updated) => {
        queryClient.setQueryData(
          getGetMyFunnelLeadV1FunnelSessionsMeLeadGetQueryKey(),
          updated
        );
      },
    },
  });

  const updateLead = useCallback(
    async (update: UpdateFunnelLeadRequest) => {
      const result = await updateMutation.mutateAsync({ data: update });
      return result;
    },
    [updateMutation]
  );

  return {
    lead,
    isLoading: isLoading || !hasSession,
    hasSession,
    updateLead,
    isUpdating: updateMutation.isPending,
  };
}
